import { tipTapContentToText } from '@lessonup/editor-shared';
import {
  useAudioCurrentTimeEventBusSubscriber,
  useAudioDurationEventBusSubscriber,
  useAudioIsPlayingEventBusSubscriber,
  useAudioRequestPlayEventBusPublisher,
  useAudioSetCurrentTimeEventBusPublisher,
  useAudioSetVolumeEventBusPublisher,
} from '@lessonup/pin-renderer';
import { AudioPinComponent, toJSONContent } from '@lessonup/pins-shared';
import { usePlayerDispatcher } from '@lessonup/players-modern';
import {
  Button,
  color,
  IconComponentAudio,
  IconPause,
  IconPlay,
  IconReset,
  IconVisibilityOff,
  IconVisibilityOn,
  styled,
} from '@lessonup/ui-components';
import { secondsToPrettyTime } from '@lessonup/utils';
import React, { useCallback, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { ButtonWithPopover } from '../../../../../components/ButtonWithPopover/ButtonWithPopover';
import { AudioVolumeSubmenu } from '../../../RealtimePlayer/components/AudioVolumeSubmenu/AudioVolumeSubmenu';
import { usePlayerTranslation } from '../../../hooks/usePlayerTranslation';
import { PlayerFAMPinComponentContentProps } from '../PlayerFAM.types';

export const AudioPinComponentPlayerFAMContent: React.FC<PlayerFAMPinComponentContentProps<AudioPinComponent>> = ({
  pinComponent,
}) => {
  const { t } = usePlayerTranslation();

  const [duration, setDuration] = useState(0);
  useAudioDurationEventBusSubscriber(pinComponent.id, setDuration);

  const [currentTime, setCurrentTimeState] = useState(0);
  useAudioCurrentTimeEventBusSubscriber(pinComponent.id, setCurrentTimeState);

  const [isPlaying, setIsPlaying] = useState(false);
  useAudioIsPlayingEventBusSubscriber(pinComponent.id, setIsPlaying);

  const startTime = pinComponent.settings.startTime || 0;
  const endTime = pinComponent.settings.endTime || duration || 0;

  const setPlayState = useAudioRequestPlayEventBusPublisher(pinComponent.id);
  const setCurrentTime = useAudioSetCurrentTimeEventBusPublisher(pinComponent.id);
  const setVolume = useAudioSetVolumeEventBusPublisher(pinComponent.id);

  const playerDispatcher = usePlayerDispatcher();

  // Calculate trimmed time values for display
  const getTrimmedCurrentTime = (): number => {
    return Math.max(0, currentTime - startTime);
  };

  const getTrimmedDuration = (): number => {
    return Math.max(0, endTime - startTime);
  };

  const bodyContent = pinComponent.settings.body && toJSONContent(pinComponent.settings.body);
  const hasBody = bodyContent ? tipTapContentToText(bodyContent).trim().length > 0 : false;

  const handlePlayPause = () => {
    if (isPlaying) {
      setPlayState(false);
    } else {
      setPlayState(true);
    }
  };

  const handleReset = () => {
    setCurrentTime(startTime);
    setPlayState(false);
  };

  const bodyShown = pinComponent.settings.showBody;
  const pinComponentId = pinComponent.id;
  const handleShowBody = () => {
    playerDispatcher({
      type: 'updatePinComponentSettings',
      pinComponentId: pinComponentId,
      settings: { showBody: !bodyShown },
    });
  };

  const updateStoreVolume = useCallback(
    (volume: number) => {
      playerDispatcher({
        type: 'updatePinComponentSettings',
        pinComponentId: pinComponentId,
        settings: { volume: volume },
      });
    },
    [playerDispatcher, pinComponentId]
  );

  const debouncedStoreUpdateVolume = useDebounceCallback(updateStoreVolume, 500);

  const handleVolumeChange = useCallback(
    (volume: number) => {
      // we both store the value and update the audio element immediately to provide instant feedback
      debouncedStoreUpdateVolume(volume);
      setVolume(volume);
    },
    [debouncedStoreUpdateVolume, setVolume]
  );

  return (
    <>
      <Button
        buttonType="neutral"
        buttonShape="rect"
        iconStart={isPlaying ? <IconPause /> : <IconPlay />}
        onClick={handlePlayPause}
        disabled={!pinComponent.settings.audioUrl}
        aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
      />
      <TimeDisplayContainer>
        {secondsToPrettyTime(getTrimmedCurrentTime())}
        {' / '}
        {secondsToPrettyTime(getTrimmedDuration())}
      </TimeDisplayContainer>
      <Button
        buttonType="neutral"
        buttonShape="rect"
        iconStart={<IconReset />}
        onClick={handleReset}
        disabled={!pinComponent.settings.audioUrl}
        aria-label={t('audioPlayer.resetAudio')}
      />
      <ButtonWithPopover
        buttonType="neutral"
        buttonShape="rect"
        iconStart={<IconComponentAudio />}
        aria-label={t('audioPlayer.volume')}
        modalConfig={{
          contentLabel: t('audioPlayer.volume'),
          modalBody: () => (
            <AudioVolumeSubmenu
              id={pinComponent.id}
              volume={pinComponent.settings.volume}
              onChange={handleVolumeChange}
            />
          ),
          modalDirection: 'top',
        }}
      />
      {hasBody && (
        <Button
          buttonType="neutral"
          buttonShape="rect"
          iconStart={bodyShown ? <IconVisibilityOff /> : <IconVisibilityOn />}
          onClick={handleShowBody}
        />
      )}
    </>
  );
};

const TimeDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: ${color.neutral.surface2.background};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: ${color.neutral.surface2.text};
  font-family: monospace;
`;
